import React, { useState, useEffect } from "react";
import Loader from "../../utils/Loader";
import { FaFacebook, FaInstagram, FaRegComment } from "react-icons/fa";
import img2 from "../../assets/images/kawa2.jpg";
import { singlePostsData } from "./socialCalendarConstants";
import FacebookPostCard from "./FacebookPostCard";
import SelectPlatforms from "./SelectPlatforms";
import SelectRegionAndOutlet from "./SelectRegionAndOutlet";
import UploadImageSection from "./UploadImageSection";
import SchedulePost from "./SchedulePost";
import CommentsAndHashtags from "./CommentsAndHashtags";
import { savePostMedia, getPlatformList } from "../../redux/slices/apiSlice";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage, getIn } from "formik";
import * as Yup from "yup";
import UseToast from "../../hooks/useToast";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { platformIds } from "../../config/constant";
import { IoMdArrowBack } from "react-icons/io";

const validationSchema = Yup.object({
  platforms: Yup.array()
    .min(1, "At least one platform is required")
    .required("This field is required"),
  accounts: Yup.array()
    .min(1, "At least one platform is required")
    .required("This field is required"),
  category: Yup.string()
    .min(1, "This field is required")
    .required("This field is required"),
  caption: Yup.string()
    .trim()
    .max(500, "Caption must be at most 500 characters")
    .required("Caption is required"),
  publishOption: Yup.string().required("Required"),
  date: Yup.string().when("publishOption", {
    is: "schedule",
    then: (schema) => schema.required("Date is required"),
  }),
  hours: Yup.number()
    .when("publishOption", {
      is: "schedule",
      then: (schema) => schema.required("Hours are required"),
    })
    .min(0, "Invalid hours")
    .max(12, "Invalid hours"),
  minutes: Yup.number()
    .when("publishOption", {
      is: "schedule",
      then: (schema) => schema.required("Minutes are required"),
    })
    .min(0, "Invalid minutes")
    .max(59, "Invalid minutes"),
  period: Yup.string().required("Required"),
  uploadFileArr: Yup.array()
    .min(1, "At least one file is required") // Ensure at least one file is selected
    .required("This field is required"),
});

const NewPost = () => {
  const { editId } = useParams();
  const [selectedCategory, setSelectedCategory] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState("schedule");
  const [selectedOutlet, setSelectedOutlet] = useState([]);
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [content, setContent] = useState("");
  const [uploadImageHover, setUploadImageHover] = useState(false);
  const dispatch = useDispatch();
  const { editedPost } = useSelector((state) => state.api);
  const [carousal, setCarousal] = useState(false);
  const [isReel, setIsReel] = useState(editId ? editedPost.postType : null);
  const [isStory, setIsStory] = useState(editId ? editedPost.postType : null);
  const [reshare, setReshare] = useState(false);

  const [selectedPlatforms, setSelectedPlatforms] = useState([
    {
      value: "facebook",
      label: "Facebook",
      icon: <FaFacebook className="text-blue-600 text-2xl" />,
    },
    {
      value: "instagram",
      label: "Instagram",
      icon: <FaInstagram className="text-pink-500 text-2xl" />,
    },
  ]);
  const { successToast } = UseToast();
  const navigate = useNavigate();
  const location = useLocation();

  const handleFileChange = (event, setFieldValue) => {
    const newFiles = event.target.files;
    if (newFiles && carousal) {
      const fileList = Array.from(newFiles); // converts FileList to File[]
      const updatedFiles = [...files, ...fileList];
      setFile(fileList[0]);
      setFiles(updatedFiles);
      setFieldValue("uploadFileArr", updatedFiles);
    } else {
      const singleFile = newFiles[0];
      setFile(singleFile);
      setFiles([singleFile]);
      setFieldValue("uploadFileArr", [singleFile]);
    }
  };

  useEffect(() => {
    dispatch(getPlatformList());
  }, []);

  useEffect(() => {
    location.pathname != "/social_calendar/new_post" &&
      (!editedPost || !Object.keys(editedPost).length) &&
      navigate("/social_calendar/new_post");
  }, [editedPost]);

  const handlePostSubmit = async (values) => {
    try {
      setIsLoading(true);
      let isoDate = "";

      if (values.date) {
        isoDate = constructFormattedDate({
          dateStr: values.date,
          hoursStr: values.hours,
          minutesStr: values.minutes,
          period: values.period,
        });
      }

      isoDate = values.datePickerObj;

      const platforms = values.platforms.map((item) => ({
        platformId: item._id,
        platformName: item.value,
      }));

      for (let i = 0; i < selectedOutlet.length; i++) {
        const outlet = selectedOutlet[i];
        const accountName = platformIds[platforms[0].platformName];

        const fileTypes = values.uploadFileArr.map(
          (file) => file.type || file.name
        );
        const isAllImages = fileTypes.every(
          (type) =>
            type.startsWith("image/") || /\.(jpg|jpeg|png|gif)$/i.test(type)
        );
        const isAllVideos = fileTypes.every(
          (type) => type.startsWith("video/") || /\.(mp4|mov|avi)$/i.test(type)
        );

        // Determine post types to create
        const postTypesToCreate = [];

        if (isReel != null && isStory != null) {
          // Both are not null, create both reel and story posts
          postTypesToCreate.push("reels", "story");
        } else if (isReel != null) {
          // Only reel is not null
          postTypesToCreate.push("reels");
        } else if (isStory != null) {
          // Only story is not null
          postTypesToCreate.push("story");
        } else {
          // Both are null, use default logic
          const postType = isAllVideos ? "reels" : "photo";
          postTypesToCreate.push(postType);
        }

        // Create posts for each post type
        for (const postType of postTypesToCreate) {
          const formData = new FormData();

          values.uploadFileArr.forEach((file) => {
            formData.append("uploadedFile", file);
          });

          formData.append("userId", "67a46351ac9fa22701c35916");
          formData.append("platforms", JSON.stringify(platforms));
          formData.append("caption", values.caption || "");
          formData.append("isPublishNow", values.publishOption === "now");
          formData.append("isAddToQueue", values.publishOption === "queue");
          formData.append("postType", postType);
          formData.append("category", selectedCategory.value);
          formData.append(
            "accounts",
            JSON.stringify(
              platforms.map((p) => ({
                accountName: outlet.label,
                accountId: platformIds[p.platformName] || "",
              }))
            )
          );
          formData.append(
            "scheduleDate",
            values.publishOption === "schedule" && isoDate
              ? JSON.stringify({
                  isScheduled: true,
                  scheduledTime: isoDate,
                })
              : ""
          );

          const response = await dispatch(savePostMedia(formData));

          if (!response?.payload?.data?._id) {
            console.error(
              `Upload failed for outlet: ${outlet.label}, postType: ${postType}`
            );
          }
        }
      }

      successToast("All posts submitted successfully.");
      navigate("/social_calendar");
      setIsLoading(false);
    } catch (error) {
      console.error("Error submitting post:", error);
      setIsLoading(false);
    }
  };

  const constructFormattedDate = ({
    dateStr,
    hoursStr,
    minutesStr,
    period,
  }) => {
    const [day, month, year] = dateStr.split("/").map(Number);

    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;

    const pad = (n) => String(n).padStart(2, "0");

    return `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${pad(minutes)}`;
  };
  const removeFile = (indx) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, index) => index !== indx);
      const lastFile =
        updatedFiles.length > 0 ? updatedFiles[updatedFiles.length - 1] : null;
      setFile(lastFile);
      return updatedFiles;
    });
  };
  const getInitialValues = () => {
    if (!editId || !editedPost || Object.keys(editedPost).length === 0) {
      return {
        platforms: [],
        caption: "",
        publishOption: "now",
        date: "",
        hours: "",
        minutes: "",
        period: "PM",
        uploadFileArr: [],
        accounts: [],
        category: [],
      };
    }
    // Extract values from editedPost (adjust these keys based on your API response)
    return {
      platforms: editedPost?.platforms || [],
      caption: editedPost?.caption || "",
      publishOption: editedPost?.isPublishNow
        ? "now"
        : editedPost?.isAddToQueue
        ? "queue"
        : "schedule",
      date: editedPost?.schedule.scheduledTime
        ? new Date(editedPost?.schedule.scheduledTime).toLocaleDateString(
            "en-GB"
          )
        : "",
      hours: editedPost?.schedule.scheduledTime
        ? (() => {
            const date = new Date(editedPost?.schedule.scheduledTime);
            const h = date.getHours();
            return h === 0 ? 12 : h > 12 ? h - 12 : h;
          })()
        : "",
      minutes: editedPost?.schedule.scheduledTime
        ? new Date(editedPost?.schedule.scheduledTime).getMinutes()
        : "",
      period: editedPost?.schedule.scheduledTime
        ? new Date(editedPost?.schedule.scheduledTime).getHours() >= 12
          ? "PM"
          : "AM"
        : "PM",
      uploadFileArr: [], // Usually handled separately
      accounts:
        editedPost?.accounts?.map((a) => ({
          label: a.accountName,
          value: a._id,
        })) || [],
      category:
        {
          label: editedPost?.category,
          value: editedPost?.category,
        } || [],
    };
  };
  const getType = (file) => {
    if (file instanceof Blob || file instanceof File) {
      return file.type.startsWith("image") ? "image" : "video";
    }

    if (typeof file === "string") {
      const lowerUrl = file.toLowerCase();
      if (
        lowerUrl.endsWith(".mp4") ||
        lowerUrl.endsWith(".webm") ||
        lowerUrl.endsWith(".mov")
      ) {
        return "video";
      }
      if (
        lowerUrl.endsWith(".png") ||
        lowerUrl.endsWith(".jpg") ||
        lowerUrl.endsWith(".jpeg") ||
        lowerUrl.endsWith(".gif") ||
        lowerUrl.endsWith(".webp")
      ) {
        return "image";
      }
    }

    return "image"; // default fallback
  };

  return (
    <>
      {isLoading && <Loader />}
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          console.log("Submitted Data:", values);
        }}
      >
        {({
          values,
          setFieldValue,
          errors,
          touched,
          setTouched,
          isValid,
          dirty,
        }) => (
          <Form>
            <div className="flex flex-col gap-4 pb-5">
              <div className="flex justify-between items-start">
                <div className="flex flex-col gap-5 justify-between items-start">
                  {" "}
                  <div>
                    <button
                      className="px-4 py-1 btn-1 h-[25px] text-xs  bg-blue-500 text-white rounded-md"
                      onClick={() => navigate("/social_calendar")}
                    >
                      <IoMdArrowBack />
                    </button>
                  </div>
                  <SelectPlatforms
                    editable={editId ? false : true}
                    values={values}
                    setFieldValue={setFieldValue}
                    editedPost={editedPost}
                  />
                  <SelectRegionAndOutlet
                    isReel={!isReel}
                    selectedCategory={selectedCategory}
                    category={editedPost.category}
                    setSelectedCategory={setSelectedCategory}
                    reshare={reshare}
                    setReshare={setReshare}
                    editable={editId ? false : true}
                    accounts={editedPost.accounts}
                    setFieldValue={setFieldValue}
                    selectedOutlet={selectedOutlet}
                    setSelectedOutlet={setSelectedOutlet}
                    isCarousal={carousal}
                    setCarousal={setCarousal}
                  />
                  <UploadImageSection
                    editable={editId ? false : true}
                    file={file}
                    setIsReel={setIsReel}
                    setIsStory={setIsStory}
                    isReel={isReel}
                    isStory={isStory}
                    isCarousal={carousal}
                    uploadImageHover={uploadImageHover}
                    setUploadImageHover={setUploadImageHover}
                    removeFile={removeFile}
                    handleFileChange={handleFileChange}
                    editedPost={editedPost}
                    files={
                      editId ? editedPost?.images?.map((img) => img.url) : files
                    }
                    setFiles={setFiles}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    touched={touched}
                    setTouched={setTouched}
                  />
                  <CommentsAndHashtags
                    editable={editId ? false : true}
                    content={content}
                    setContent={(e) => setContent(e.target.value)}
                    values={values}
                    setFieldValue={setFieldValue}
                    editedPost={editedPost}
                  />
                </div>
                {/* right side view */}
                <div className="flex flex-col gap-5 ml-2 items-start">
                  <SchedulePost
                    editable={editId ? false : true}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    values={values}
                    setFieldValue={setFieldValue}
                    handlePostSubmit={handlePostSubmit}
                    isValid={isValid}
                    dirty={dirty}
                    editedPost={editedPost}
                  />
                  <div className="max-w-[300px]">
                    <FacebookPostCard
                      width={"min-w-[300px]"}
                      editPost={editId ? false : true}
                      content={content}
                      file={
                        files?.length > 0
                          ? files.map((f) => {
                              const isBlob =
                                f instanceof File || f instanceof Blob;
                              const url = isBlob ? URL.createObjectURL(f) : f;
                              const type = getType(f);
                              return { url, type };
                            })
                          : editId
                          ? editedPost?.images?.map((img) => ({
                              url: img?.url,
                              type: getType(img?.url),
                            }))
                          : img2
                          ? [{ url: img2, type: getType(img2) }]
                          : []
                      }
                      fileType={getType(
                        file
                          ? file
                          : editId
                          ? editedPost?.images?.[0]?.url
                          : img2
                      )}
                      type={
                        editId
                          ? editedPost?.platforms?.[0]?.platformName
                          : values?.platforms?.length > 0
                          ? values.platforms[0]?.label
                          : "Facebook"
                      }
                      post={editId ? editedPost : singlePostsData}
                      username={
                        editId
                          ? editedPost?.accounts?.length > 0
                            ? editedPost?.accounts[0]?.accountName
                            : ""
                          : selectedOutlet?.length > 0
                          ? selectedOutlet[0]?.label
                          : ""
                      }
                      caption={values.caption}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default NewPost;
